<template>
  <div
    class="relative flex h-screen w-full max-w-full overflow-hidden bg-slate-50 dark:bg-slate-900"
  >
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :is-open-mobile="isSidebarOpenMobile"
      :sidebar-search="sidebarSearch"
      :lang="lang"
      :t="t"
      @update:sidebar-search="sidebarSearch = $event"
      @update:is-open-mobile="isSidebarOpenMobile = $event"
    />

    <div class="flex flex-col flex-1 min-w-0 min-h-0 w-full overflow-y-auto custom-scrollbar">
      <AdminTopbar
        :is-collapsed="isSidebarCollapsed"
        :lang="lang"
        :t="t"
        @toggle-sidebar="toggleSidebar"
      />

      <main class="flex-1 min-w-0 px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        <div class="max-w-6xl mx-auto space-y-4">
          <div
            class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <h1 class="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">
                {{ t("ticket_my_title") }}
              </h1>
              <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                {{ t("ticket_my_subtitle") }}
              </p>
            </div>
            <RouterLink
              v-if="hasLoadedOnce"
              to="/tickets/create"
              class="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2.5 sm:py-2 text-xs font-medium text-white rounded-xl shadow-sm bg-primary hover:bg-primary/90 transition"
            >
              <i
                class="mdi mdi-plus-circle-outline text-[16px]"
                :class="lang === 'ar' ? 'ml-1.5' : 'mr-1.5'"
              ></i>
              {{ t("ticket_create_cta") }}
            </RouterLink>
          </div>

          <section
            class="overflow-hidden bg-white border shadow-sm dark:bg-slate-900 border-slate-200/80 dark:border-slate-800 rounded-2xl"
          >
            <header
              class="px-3.5 py-3 sm:px-5 border-b border-slate-200/60 dark:border-slate-700/60 bg-white/60 dark:bg-slate-900/40 backdrop-blur-sm"
            >
              <div class="flex flex-col md:flex-row md:items-center gap-3 min-w-0">
                <FilterSearchInput
                  v-model="searchInput"
                  :lang="lang"
                  :placeholder="t('ticket_search_placeholder')"
                  :aria-label="t('ticket_search_hint')"
                  :clear-title="lang === 'ar' ? 'مسح' : 'Clear'"
                  @clear="clearSearch"
                  class="w-full md:w-64 lg:w-72 shrink-0"
                />

                <div class="flex-1 min-w-0 overflow-x-auto custom-scrollbar -mx-1 px-1 py-0.5">
                  <TicketFilterPills
                    class="min-w-max"
                    :pills="filterPills"
                    :active-key="activePill"
                    :aria-label="t('ticket_filter_label')"
                    @update:active-key="onPillChange"
                  />
                </div>
              </div>

              <div
                v-if="hasActiveFilters"
                class="mt-3 pt-3 border-t border-slate-200/50 dark:border-slate-700/50"
              >
                <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <div
                    class="flex shrink-0 items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400"
                  >
                    <span
                      class="flex items-center justify-center w-6 h-6 rounded-lg bg-primary/10 text-primary"
                    >
                      <i class="mdi mdi-filter-variant text-[14px]"></i>
                    </span>
                    <span class="font-medium text-slate-600 dark:text-slate-300">
                      {{ t("ticket_filter_active_hint") }}
                    </span>
                    <span
                      v-if="!isLoading && meta.total != null"
                      class="tabular-nums font-semibold text-primary"
                    >
                      ({{ meta.total }})
                    </span>
                  </div>

                  <div class="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                    <button
                      v-for="chip in activeFilterChips"
                      :key="chip.id"
                      type="button"
                      class="group inline-flex items-center gap-1 h-8 max-w-full pl-2.5 pr-1 text-[11px] font-medium rounded-lg border transition-colors border-slate-200/90 bg-white dark:bg-slate-800/80 dark:border-slate-700/80 text-slate-700 dark:text-slate-200 hover:border-primary/35 hover:bg-primary/5 dark:hover:bg-primary/10"
                      :title="t('ticket_filter_clear')"
                      @click="removeFilterChip(chip.id)"
                    >
                      <i
                        v-if="chip.icon"
                        class="mdi shrink-0 text-[13px] text-primary/70"
                        :class="chip.icon"
                      ></i>
                      <span class="shrink-0 text-slate-400 dark:text-slate-500">
                        {{ chip.label }}:
                      </span>
                      <span class="truncate max-w-[10rem]">{{ chip.value }}</span>
                      <span
                        class="flex shrink-0 items-center justify-center w-6 h-6 rounded-md text-slate-400 transition-colors group-hover:text-primary group-hover:bg-primary/10"
                      >
                        <i class="mdi mdi-close text-[14px]"></i>
                      </span>
                    </button>
                  </div>

                  <button
                    type="button"
                    class="shrink-0 inline-flex items-center gap-1 h-8 px-2.5 text-[11px] font-semibold rounded-lg text-primary hover:bg-primary/8 transition-colors"
                    @click="clearFilters"
                  >
                    <i class="mdi mdi-filter-off-outline text-[14px]"></i>
                    {{ t("ticket_filter_clear_all") }}
                  </button>
                </div>
              </div>
            </header>

            <div class="p-3.5 sm:p-5">
              <div
                v-if="isInitialLoading"
                class="flex flex-col items-center justify-center py-14 text-center"
              >
                <i class="mb-3 text-3xl mdi mdi-loading mdi-spin text-primary/70"></i>
                <p class="text-sm text-slate-500">{{ t("loading") }}</p>
              </div>

              <div
                v-else-if="!tickets.length"
                class="flex flex-col items-center justify-center px-4 py-10 text-center border border-dashed rounded-2xl border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40"
              >
                <i
                  class="mb-3 text-3xl mdi mdi-ticket-confirmation-outline text-secondary"
                ></i>
                <p class="text-sm font-medium text-slate-700 dark:text-slate-200">
                  {{
                    hasActiveFilters
                      ? t("ticket_table_empty_filtered")
                      : t("ticket_table_empty")
                  }}
                </p>
                <p
                  v-if="!hasActiveFilters"
                  class="mt-1 max-w-sm text-xs text-slate-500 dark:text-slate-400"
                >
                  {{ t("ticket_table_empty_hint") }}
                </p>
                <button
                  v-if="hasActiveFilters"
                  type="button"
                  class="inline-flex items-center gap-1.5 px-4 py-2 mt-4 text-xs font-semibold rounded-lg text-primary bg-primary/10 hover:bg-primary/15 transition-colors"
                  @click="clearFilters"
                >
                  <i class="mdi mdi-filter-off-outline text-[16px]"></i>
                  {{ t("ticket_filter_clear_all") }}
                </button>
                <RouterLink
                  v-else
                  to="/tickets/create"
                  class="inline-flex items-center px-4 py-2 mt-4 text-xs font-medium text-white rounded-lg bg-primary hover:bg-primary/90"
                >
                  {{ t("ticket_create_cta") }}
                </RouterLink>
              </div>

              <!-- Tickets Data View (Table on Desktop / Cards on Mobile) -->
              <div
                v-else
                class="rounded-2xl overflow-hidden"
                :class="isRefreshing ? 'opacity-60 pointer-events-none' : ''"
              >
                <!-- 📱 Mobile Card View (shown on mobile screens < md) -->
                <div class="block md:hidden space-y-3">
                  <div
                    v-for="ticket in tickets"
                    :key="ticket.ticket_number || ticket.id"
                    class="p-4 rounded-xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm hover:border-primary/30 transition flex flex-col gap-3"
                  >
                    <!-- Top row: Number Badge & Status Badge -->
                    <div class="flex items-center justify-between gap-2">
                      <TicketNumberBadge
                        :ticket-number="ticket.ticket_number"
                        :status-class="numberBadgeClass(ticket)"
                      />

                      <TicketStatusBadge
                        :status="ticket.status"
                        :unassigned="isUnassigned(ticket)"
                        :lang="lang"
                        :t="t"
                      />
                    </div>

                    <!-- Subject & Overdue warning -->
                    <div class="flex flex-col gap-1">
                      <h3
                        class="text-sm font-semibold text-slate-900 dark:text-white leading-snug break-words"
                      >
                        {{ ticket.subject || "—" }}
                      </h3>
                      <span
                        v-if="ticket.is_overdue"
                        class="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-600 dark:text-rose-400"
                      >
                        <i class="mdi mdi-clock-alert-outline text-[13px]"></i>
                        {{ t("ticket_overdue") }}
                      </span>
                    </div>

                    <!-- Meta Row: Category/Type + Date -->
                    <div class="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-100 dark:border-slate-800 text-[11px]">
                      <span
                        class="inline-flex items-center gap-1 px-2 py-0.5 font-medium rounded-lg border bg-white dark:bg-slate-900 border-primary/40 text-primary bg-primary/5"
                      >
                        <i class="mdi mdi-shape-outline text-[13px] shrink-0"></i>
                        <span>{{ typeLabel(ticket) }}</span>
                      </span>

                      <span class="text-slate-500 dark:text-slate-400 tabular-nums">
                        {{
                          formatTicketDateTime(
                            ticket.updated_at || ticket.created_at,
                            lang,
                          )
                        }}
                      </span>
                    </div>

                    <!-- Action Button -->
                    <div class="pt-2 border-t border-slate-100 dark:border-slate-800">
                      <button
                        type="button"
                        class="w-full inline-flex items-center justify-center gap-1.5 h-9 px-3 text-xs font-semibold rounded-lg bg-primary/10 hover:bg-primary/15 text-primary transition"
                        @click="openTicket(ticket)"
                      >
                        <i class="mdi mdi-eye-outline text-[16px]"></i>
                        <span>{{ t("ticket_view") }}</span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- 🖥️ Desktop / Tablet Data Table (shown on md+ screens) -->
                <div class="hidden md:block border rounded-2xl border-slate-100 dark:border-slate-800 overflow-hidden">
                  <div class="overflow-x-auto custom-scrollbar">
                    <table class="w-full min-w-[840px] text-sm table-fixed">
                      <colgroup>
                        <col class="w-[15%]" />
                        <col class="w-[14%]" />
                        <col class="w-[28%]" />
                        <col class="w-[16%]" />
                        <col class="w-[17%]" />
                        <col class="w-[10%]" />
                      </colgroup>
                      <thead
                        class="sticky top-0 z-10 text-xs font-medium tracking-wide uppercase text-slate-600 bg-secondary/10 dark:bg-slate-800/90 dark:text-slate-200"
                      >
                        <tr>
                          <th
                            class="px-3.5 py-3 whitespace-nowrap"
                            :class="lang === 'ar' ? 'text-right' : 'text-left'"
                          >
                            {{ t("ticket_table_number") }}
                          </th>
                          <th
                            class="px-3.5 py-3 whitespace-nowrap"
                            :class="lang === 'ar' ? 'text-right' : 'text-left'"
                          >
                            {{ t("ticket_table_type") }}
                          </th>
                          <th
                            class="px-3.5 py-3"
                            :class="lang === 'ar' ? 'text-right' : 'text-left'"
                          >
                            {{ t("ticket_table_subject") }}
                          </th>
                          <th
                            class="px-3.5 py-3 whitespace-nowrap"
                            :class="lang === 'ar' ? 'text-right' : 'text-left'"
                          >
                            {{ t("ticket_table_status") }}
                          </th>
                          <th
                            class="px-3.5 py-3 whitespace-nowrap"
                            :class="lang === 'ar' ? 'text-right' : 'text-left'"
                          >
                            {{ t("ticket_table_updated") }}
                          </th>
                          <th class="px-3.5 py-3 text-center whitespace-nowrap">
                            {{ t("ticket_table_actions") }}
                          </th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
                        <tr
                          v-for="ticket in tickets"
                          :key="ticket.ticket_number || ticket.id"
                          class="transition-colors hover:bg-primary/5 dark:hover:bg-slate-800/60 even:bg-slate-50/60 dark:even:bg-slate-900/40"
                        >
                          <td class="px-3.5 py-3 align-middle whitespace-nowrap">
                            <TicketNumberBadge
                              :ticket-number="ticket.ticket_number"
                              :status-class="numberBadgeClass(ticket)"
                            />
                          </td>
                          <td class="px-3.5 py-3 align-middle min-w-0">
                            <span
                              class="inline-flex items-start gap-1 max-w-full min-w-0 px-2 py-1 text-[11px] font-medium leading-snug rounded-lg border bg-white dark:bg-slate-900 border-primary/40 text-primary bg-primary/5"
                            >
                              <i
                                class="mdi mdi-shape-outline text-[14px] shrink-0 mt-0.5"
                              ></i>
                              <span class="break-words">{{ typeLabel(ticket) }}</span>
                            </span>
                          </td>
                          <td class="px-3.5 py-3 align-middle min-w-0">
                            <p
                              class="text-xs font-medium text-slate-800 dark:text-slate-100 break-words"
                            >
                              {{ ticket.subject || "—" }}
                            </p>
                            <span
                              v-if="ticket.is_overdue"
                              class="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-rose-600"
                            >
                              <i class="mdi mdi-clock-alert-outline"></i>
                              {{ t("ticket_overdue") }}
                            </span>
                          </td>
                          <td class="px-3.5 py-3 align-middle whitespace-nowrap">
                            <TicketStatusBadge
                              :status="ticket.status"
                              :unassigned="isUnassigned(ticket)"
                              :lang="lang"
                              :t="t"
                            />
                          </td>
                          <td
                            class="px-3.5 py-3 align-middle text-[11px] text-slate-500 tabular-nums whitespace-nowrap"
                          >
                            {{
                              formatTicketDateTime(
                                ticket.updated_at || ticket.created_at,
                                lang,
                              )
                            }}
                          </td>
                          <td class="px-3.5 py-3 align-middle text-center whitespace-nowrap">
                            <div
                              class="relative inline-flex items-center justify-center"
                              :class="openActionId === (ticket.ticket_number || ticket.id) ? 'z-30' : 'z-0'"
                            >
                              <button
                                type="button"
                                class="inline-flex items-center justify-center w-8 h-8 transition bg-white border rounded-full text-slate-500 dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:text-primary hover:border-primary/40 hover:bg-primary/5"
                                :aria-label="t('ticket_table_actions')"
                                @click.stop="toggleActionMenu(ticket.ticket_number || ticket.id)"
                              >
                                <i class="mdi mdi-dots-horizontal text-[18px]"></i>
                              </button>

                              <div
                                v-if="openActionId === (ticket.ticket_number || ticket.id)"
                                class="absolute end-0 bottom-full mb-1 z-50 w-40 p-1 text-xs bg-white border shadow-lg rounded-xl dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-100"
                                @click.stop
                              >
                                <button
                                  type="button"
                                  class="flex items-center w-full px-3 py-2 text-left transition rounded-lg hover:bg-secondary/10 hover:text-secondary"
                                  @click="openTicket(ticket)"
                                >
                                  <i
                                    class="mdi mdi-eye-outline text-[16px]"
                                    :class="lang === 'ar' ? 'ml-2' : 'mr-2'"
                                  ></i>
                                  <span>{{ t("ticket_view") }}</span>
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Pagination -->
                <div
                  class="flex flex-col gap-3 px-4 py-3 bg-white border-t dark:bg-slate-900 border-slate-100 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between mt-3 md:mt-0"
                >
                  <div class="text-xs text-slate-600 dark:text-slate-300">
                    <span v-if="pagination.total">
                      {{ lang === "ar" ? "عرض" : "Showing" }}
                      <b>{{ pagination.from }}</b
                      >–<b>{{ pagination.to }}</b>
                      {{ lang === "ar" ? "من" : "of" }}
                      <b>{{ pagination.total }}</b>
                    </span>
                    <span v-else>—</span>
                  </div>

                  <div
                    class="flex flex-wrap items-center justify-between sm:justify-end gap-3"
                  >
                    <div class="flex items-center gap-2">
                      <span class="text-xs text-slate-500 dark:text-slate-400">
                        {{ lang === "ar" ? "عدد الصفوف" : "Rows" }}
                      </span>
                      <CompactSelect
                        v-model="perPage"
                        :options="perPageSelectOptions"
                        min-width="4.5rem"
                        align="start"
                      />
                    </div>

                    <div class="flex items-center justify-center gap-1">
                      <button
                        type="button"
                        @click="prevPage"
                        :disabled="pagination.current <= 1 || isLoading"
                        class="inline-flex items-center justify-center text-xs bg-white border rounded-lg w-9 h-9 dark:bg-slate-900 border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800"
                        aria-label="Previous Page"
                      >
                        <i
                          class="mdi mdi-chevron-left"
                          :class="lang === 'ar' ? 'rotate-180' : ''"
                        ></i>
                      </button>

                      <button
                        v-if="pagesToShow.showFirst"
                        type="button"
                        @click="goToPage(1)"
                        class="text-xs bg-white border rounded-lg w-9 h-9 dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        1
                      </button>
                      <span
                        v-if="pagesToShow.showLeftDots"
                        class="px-1 text-slate-400"
                        >…</span
                      >

                      <button
                        v-for="p in pagesToShow.pages"
                        :key="p"
                        type="button"
                        @click="goToPage(p)"
                        class="text-xs transition border rounded-lg w-9 h-9"
                        :class="
                          p === pagination.current
                            ? 'bg-primary/10 text-primary border-primary/30'
                            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'
                        "
                      >
                        {{ p }}
                      </button>

                      <span
                        v-if="pagesToShow.showRightDots"
                        class="px-1 text-slate-400"
                        >…</span
                      >
                      <button
                        v-if="pagesToShow.showLast"
                        type="button"
                        @click="goToPage(pagination.last)"
                        class="text-xs bg-white border rounded-lg w-9 h-9 dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        {{ pagination.last }}
                      </button>

                      <button
                        type="button"
                        @click="nextPage"
                        :disabled="
                          pagination.current >= pagination.last || isLoading
                        "
                        class="inline-flex items-center justify-center text-xs bg-white border rounded-lg w-9 h-9 dark:bg-slate-900 border-slate-200 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800"
                        aria-label="Next Page"
                      >
                        <i
                          class="mdi mdi-chevron-right"
                          :class="lang === 'ar' ? 'rotate-180' : ''"
                        ></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>

    <Toast
      :show="toast.show"
      :type="toast.type"
      :message="toast.message"
      @update:show="toast.show = $event"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import AdminSidebar from "@/components/Auth/Sidebar.vue";
import AdminTopbar from "@/components/Auth/TopNav.vue";
import CompactSelect from "@/components/CompactSelect.vue";
import Toast from "@/components/Toast.vue";
import FilterSearchInput from "@/components/FilterSearchInput.vue";
import TicketFilterPills from "@/components/tickets/TicketFilterPills.vue";
import TicketNumberBadge from "@/components/tickets/TicketNumberBadge.vue";
import TicketStatusBadge from "@/components/tickets/TicketStatusBadge.vue";
import { ticketsApi } from "@/api/tickets";
import { useTicketMetaStore } from "@/stores/ticketMeta";
import { usePageI18n } from "@/composables/usePageI18n";
import { useAuthShell } from "@/composables/useAuthShell";
import {
  pickTranslatedName,
  formatTicketDateTime,
  getStatusCode,
  statusBadgeClasses,
  // creatorDisplayName,
} from "@/utils/ticket";

const { lang, t } = usePageI18n();
const {
  isSidebarCollapsed,
  isSidebarOpenMobile,
  sidebarSearch,
  toggleSidebar,
  toast,
  showToast,
} = useAuthShell();

const router = useRouter();
const ticketMeta = useTicketMetaStore();

const isLoading = ref(true);
const hasLoadedOnce = ref(false);
const tickets = ref([]);
const stats = ref({});

const page = ref(1);
const perPage = ref(10);

const perPageSelectOptions = [
  { value: 10, label: "10" },
  { value: 20, label: "20" },
  { value: 30, label: "30" },
  { value: 50, label: "50" },
];

const meta = ref({
  current_page: 1,
  last_page: 1,
  per_page: 10,
  total: 0,
});

const pagination = computed(() => {
  const m = meta.value || {};

  const total = Number(m.total ?? m.total_items ?? 0);
  const current = Number(m.current_page ?? m.page ?? page.value ?? 1);
  const per = Number(m.per_page ?? m.perPage ?? perPage.value ?? 10);
  const last = Number(m.last_page ?? Math.max(1, Math.ceil(total / per)));

  const from = Number(m.from ?? (total ? (current - 1) * per + 1 : 0));
  const to = Number(m.to ?? Math.min(current * per, total));

  return { total, current, per, last, from, to };
});

const pagesToShow = computed(() => {
  const { current, last } = pagination.value;
  const delta = 2;
  const start = Math.max(1, current - delta);
  const end = Math.min(last, current + delta);

  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);

  return {
    showFirst: start > 1,
    showLeftDots: start > 2,
    pages,
    showRightDots: end < last - 1,
    showLast: end < last,
  };
});

function goToPage(p) {
  const n = Number(p);
  if (!n) return;
  if (n < 1 || n > pagination.value.last) return;
  page.value = n;
  meta.value.current_page = n;
  loadTickets();
}

function prevPage() {
  if (pagination.value.current > 1) goToPage(pagination.value.current - 1);
}

function nextPage() {
  if (pagination.value.current < pagination.value.last)
    goToPage(pagination.value.current + 1);
}

const searchInput = ref("");
const debouncedSearch = ref("");
const activePill = ref("all");
let searchTimer = null;

const isInitialLoading = computed(() => isLoading.value && !hasLoadedOnce.value);
const isRefreshing = computed(() => isLoading.value && hasLoadedOnce.value);

watch(perPage, () => {
  page.value = 1;
  meta.value.current_page = 1;
  loadTickets();
});

watch(searchInput, (val) => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    debouncedSearch.value = val.trim();
    page.value = 1;
    meta.value.current_page = 1;
    loadTickets();
  }, 300);
});

const filterPills = computed(() => [
  {
    key: "all",
    label: t("ticket_filter_all"),
    count: stats.value.total ?? null,
    icon: "mdi-view-grid-outline",
  },
  {
    key: "new",
    label: t("ticket_filter_new"),
    count: stats.value.unassigned_in_queue ?? stats.value.open ?? null,
    icon: "mdi-star-four-points-outline",
  },
  {
    key: "in_progress",
    label:
      pickTranslatedName(
        ticketMeta.statuses.find((s) => getStatusCode(s) === "in_progress"),
        lang.value,
      ) || (lang.value === "ar" ? "قيد المعالجة" : "In progress"),
    count: stats.value.in_progress ?? null,
    icon: "mdi-progress-clock",
  },
  {
    key: "resolved",
    label:
      pickTranslatedName(
        ticketMeta.statuses.find((s) => getStatusCode(s) === "resolved"),
        lang.value,
      ) || (lang.value === "ar" ? "محلول" : "Resolved"),
    count: stats.value.resolved ?? null,
    icon: "mdi-check-circle-outline",
  },
  {
    key: "escalated",
    label:
      pickTranslatedName(
        ticketMeta.statuses.find((s) => getStatusCode(s) === "escalated"),
        lang.value,
      ) || (lang.value === "ar" ? "مُصعّدة" : "Escalated"),
    count: stats.value.escalated ?? null,
    icon: "mdi-arrow-up-bold-circle-outline",
  },
]);

const hasActiveFilters = computed(
  () => activePill.value !== "all" || debouncedSearch.value,
);

const activeFilterChips = computed(() => {
  const chips = [];

  if (activePill.value !== "all") {
    const pill = filterPills.value.find((p) => p.key === activePill.value);
    chips.push({
      id: "status",
      label: t("ticket_filter_status"),
      value: pill?.label || activePill.value,
      icon: pill?.icon || "mdi-label-outline",
    });
  }

  if (debouncedSearch.value) {
    chips.push({
      id: "search",
      label: t("ticket_filter_search"),
      value: debouncedSearch.value,
      icon: "mdi-magnify",
    });
  }

  return chips;
});

function buildFilterParams() {
  const params = {
    page: page.value,
    itemsPerPage: perPage.value,
    per_page: perPage.value,
    perPage: perPage.value,
    orderBy: "desc",
    sortBy: "updated_at",
  };

  if (debouncedSearch.value) params.search = debouncedSearch.value;

  const statusByCode = (code) =>
    ticketMeta.statuses.find((s) => getStatusCode(s) === code);

  switch (activePill.value) {
    case "new":
      params.unassigned = 1;
      const open = statusByCode("open");
      if (open?.id) params.status = open.id;
      break;
    case "in_progress":
      const ip = statusByCode("in_progress");
      if (ip?.id) params.status = ip.id;
      break;
    case "resolved":
      const res = statusByCode("resolved");
      if (res?.id) params.status = res.id;
      break;
    case "escalated":
      const esc = statusByCode("escalated");
      if (esc?.id) params.status = esc.id;
      break;
    default:
      break;
  }

  return params;
}

async function loadTickets() {
  isLoading.value = true;
  try {
    const res = await ticketsApi.myTickets(buildFilterParams());
    tickets.value = res.data || [];
    stats.value = res.stats || {};
    meta.value = {
      current_page: res.meta?.current_page ?? page.value ?? 1,
      last_page: res.meta?.last_page ?? 1,
      per_page: res.meta?.per_page ?? perPage.value ?? 10,
      total: res.meta?.total ?? tickets.value.length,
      from: res.meta?.from ?? (res.meta?.total ? (Number(page.value) - 1) * Number(perPage.value) + 1 : 0),
      to: res.meta?.to ?? Math.min(Number(page.value) * Number(perPage.value), Number(res.meta?.total ?? tickets.value.length)),
    };
  } catch (e) {
    console.error(e);
    showToast(t("ticket_load_failed"), "error");
  } finally {
    isLoading.value = false;
    hasLoadedOnce.value = true;
  }
}

function onPillChange(key) {
  activePill.value = key;
  page.value = 1;
  meta.value.current_page = 1;
  loadTickets();
}

function clearSearch() {
  searchInput.value = "";
  debouncedSearch.value = "";
  page.value = 1;
  meta.value.current_page = 1;
  loadTickets();
}

function clearFilters() {
  activePill.value = "all";
  if (searchInput.value) {
    searchInput.value = "";
    debouncedSearch.value = "";
  }
  page.value = 1;
  meta.value.current_page = 1;
  loadTickets();
}

function removeFilterChip(id) {
  if (id === "status") {
    activePill.value = "all";
    page.value = 1;
    meta.value.current_page = 1;
    loadTickets();
  } else if (id === "search") {
    clearSearch();
  }
}

function isUnassigned(ticket) {
  return !ticket.assigned_to && getStatusCode(ticket.status) === "open";
}

function numberBadgeClass(ticket) {
  return statusBadgeClasses(ticket.status, {
    unassigned: isUnassigned(ticket),
  });
}

const openActionId = ref(null);

function toggleActionMenu(id) {
  openActionId.value = openActionId.value === id ? null : id;
}

function handleClickOutside() {
  if (openActionId.value !== null) openActionId.value = null;
}

function openTicket(ticket) {
  openActionId.value = null;
  router.push({
    name: "tickets.show",
    params: { ticketNumber: ticket.ticket_number },
  });
}

function typeLabel(ticket) {
  const cat = pickTranslatedName(ticket.category, lang.value);
  if (cat) return cat;
  const type = String(ticket.creator_type || "").toLowerCase();
  if (type === "client") return lang.value === "ar" ? "عميل" : "Client";
  if (type.includes("consultant") || type.includes("office")) {
    return lang.value === "ar" ? "مستشار" : "Consultant";
  }
  return "—";
}

/*
function partyLabel(ticket) {
  const dept = pickTranslatedName(ticket.department, lang.value);
  if (dept) return dept;
  return creatorDisplayName(ticket, lang.value);
}
*/

onMounted(async () => {
  document.addEventListener("click", handleClickOutside);
  await ticketMeta.fetchMetadataOnce();
  loadTickets();
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>
